// ============================================================
// CLUBHUB – Suchfeld ueber den Einstellungen
// ============================================================
// Gesucht wird im Register selbst (`lib/einstellungen/search.ts`).
// Pfeiltasten, Eingabe, Escape — und der Treffer hebt das Feld im
// Register hervor.
// ============================================================

"use client";

import { useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { noAutocorrect } from "@/components/ui/no-autocorrect";
import type { Permission } from "@/lib/permissions";
import { searchSettings, type SettingsHit } from "@/lib/einstellungen/search";

export function SettingsSearch({ rechte }: { rechte: Permission[] }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(0);
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);

  const hits = useMemo(() => searchSettings(query, rechte, 8), [query, rechte]);

  function goTo(target: SettingsHit) {
    setQuery("");
    setOpen(false);
    inputRef.current?.blur();
    router.push(target.href);
  }

  return (
    <div className="relative w-full sm:max-w-xs">
      <Search
        className="text-text-tertiary pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2"
        aria-hidden
      />
      <input
        ref={inputRef}
        type="search"
        {...noAutocorrect}
        value={query}
        role="combobox"
        aria-expanded={open && hits.length > 0}
        aria-controls="einstellungen-treffer"
        aria-label="Einstellungen durchsuchen"
        placeholder="Einstellung suchen …"
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
          setSelected(0);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setSelected((position) => Math.min(position + 1, hits.length - 1));
          } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setSelected((position) => Math.max(position - 1, 0));
          } else if (event.key === "Enter" && hits[selected]) {
            event.preventDefault();
            goTo(hits[selected]);
          } else if (event.key === "Escape") {
            setOpen(false);
          }
        }}
        className={cn(
          "bg-bg-primary text-body-sm text-text-primary h-9 w-full rounded-lg border pr-3 pl-9",
          "border-border placeholder:text-text-tertiary",
          "duration-fast ease-standard transition-[box-shadow,border-color]",
          "hover:border-border-strong focus:border-accent/40 focus:ring-accent/30 focus:ring-2 focus:outline-none",
        )}
      />

      {open && query.trim() !== "" && (
        <div
          id="einstellungen-treffer"
          role="listbox"
          className="bg-bg-elevated border-border shadow-e3 absolute top-11 right-0 left-0 z-30 overflow-hidden rounded-xl border py-1"
        >
          {hits.length === 0 ? (
            <p className="text-body-sm text-text-tertiary px-3 py-2">Keine Einstellung gefunden.</p>
          ) : (
            hits.map((entry, position) => (
              <button
                key={`${entry.id}-${position}`}
                type="button"
                role="option"
                aria-selected={position === selected}
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => setSelected(position)}
                onClick={() => goTo(entry)}
                className={cn(
                  "block w-full px-3 py-2 text-left",
                  position === selected ? "bg-bg-secondary" : "hover:bg-bg-secondary",
                )}
              >
                <span className="text-body-sm text-text-primary block truncate">{entry.label}</span>
                <span className="text-caption text-text-tertiary block truncate">{entry.sub}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
